import {
  INewbuildingPolygon,
  INewbuildingsPolygons,
  TNewbuildingPolygonFeature,
} from '../../../types/map/newbuildingPolygon';

export function defineNewbuildingsPolygonsManager(ymaps: YMaps.IYMaps) {
  if (ymaps.modules.isDefined('defineNewbuildingsPolygonsManager')) {
    return;
  }

  ymaps.modules.define('NewbuildingsPolygonsManager', [], provide => {
    const POLYGON_COLORS = {
      desktop: {
        default: 'rgba(4, 104, 255, 0.10)',
        active: 'rgba(4, 104, 255, 0.20)',
      },
      mobile: {
        default: 'rgba(4, 104, 255, 0)',
        active: 'rgba(4, 104, 255, 0.10)',
      },
    };

    class NewbuildingsPolygonsManager {
      public readonly objectManager: YMaps.ObjectManager;
      private activeNewbuildingId: number | null = null;
      private hoveredNewbuildingId: number | null = null;
      private readonly isDesktop: boolean;

      public constructor(isDesktop: boolean = true) {
        this.objectManager = new ymaps.ObjectManager();
        this.isDesktop = isDesktop;
      }

      public createPolygons = (polygons: INewbuildingsPolygons) => {
        const features = this.prepareFeatures(polygons);

        this.objectManager.add({
          type: 'FeatureCollection',
          features,
        });
      };

      public clear = () => {
        this.objectManager.removeAll();
        this.activeNewbuildingId = null;
        this.hoveredNewbuildingId = null;
      };

      public setActivePolygon = (newbuildingId: number | null) => {
        const prevActiveId = this.activeNewbuildingId;
        this.activeNewbuildingId = newbuildingId;

        // Обновляем стили предыдущего активного полигона
        if (prevActiveId !== null && prevActiveId !== newbuildingId) {
          this.updatePolygonOptions(prevActiveId);
        }

        // Обновляем стили нового активного полигона
        if (newbuildingId !== null) {
          this.updatePolygonOptions(newbuildingId);
        }
      };

      public setHoveredPolygon = (newbuildingId: number | null) => {
        const prevHoveredId = this.hoveredNewbuildingId;
        this.hoveredNewbuildingId = newbuildingId;

        // Обновляем стили предыдущего наведенного полигона
        if (prevHoveredId !== null && prevHoveredId !== newbuildingId) {
          this.updatePolygonOptions(prevHoveredId);
        }

        // Обновляем стили нового наведенного полигона
        if (newbuildingId !== null) {
          this.updatePolygonOptions(newbuildingId);
        }
      };

      private updatePolygonOptions = (newbuildingId: number) => {
        const objectId = String(newbuildingId);
        const options = this.getPolygonOptions(newbuildingId);

        this.objectManager.objects.setObjectOptions(objectId, options);
      };

      private getPolygonOptions = (newbuildingId: number): YMaps.IPolygonOptions => {
        const isActive = this.activeNewbuildingId === newbuildingId;
        const isHovered = this.hoveredNewbuildingId === newbuildingId;

        const colors = this.isDesktop ? POLYGON_COLORS.desktop : POLYGON_COLORS.mobile;
        const fillColor = isHovered || isActive ? colors.active : colors.default;

        return {
          fillColor,
          interactivityModel: 'default#transparent',
          strokeWidth: 1,
          strokeColor: this.isDesktop ? '#3686FF' : '#2178FD',
        };
      };

      private prepareFeatures = (polygons: INewbuildingsPolygons) => {
        const polygonsIds = Object.keys(polygons);

        return polygonsIds.map(id => this.transformPolygonToFeature(polygons[id]));
      };

      private transformPolygonToFeature(polygon: INewbuildingPolygon): TNewbuildingPolygonFeature {
        const { coordinates, newbuildingId } = polygon;

        return {
          type: 'Feature',
          id: String(newbuildingId),
          properties: {
            newbuildingId,
          },
          options: this.getPolygonOptions(newbuildingId),
          geometry: {
            type: 'Polygon',
            coordinates: [coordinates] as GeoJSON.Position[][],
          },
        };
      }
    }

    provide(NewbuildingsPolygonsManager);
  });
}
